import { CaretDownIcon, EyeIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AssetResponse } from "../generated/api/types.gen";
import { EMPTY_MESSAGE } from "../lib/asset-modal-tabs";
import { searchAssets } from "../lib/asset-search";

/** Max number of results rendered in the dropdown (keeps large feeds snappy). */
const RESULT_CAP = 50;

export interface ColorAssetPickerProps {
  assets: AssetResponse[];
  isLoading: boolean;
  isError: boolean;
  selectedAssetId: string | null;
  onSelect: (asset: AssetResponse) => void;
  onRetry: () => void;
  emptyMessage?: string;
  /** Assets attached to the current project. When given, results split into
   *  "Assets in this folder" and "All Assets". */
  projectAssetIds?: ReadonlySet<string>;
  /** When given, image rows get an eye button that opens a larger view.
   *  Hex-valued assets have nothing to enlarge, so they never get one. */
  onPreview?: (asset: AssetResponse) => void;
}

function AssetThumb({ asset }: { asset: AssetResponse }) {
  if (asset.type === "image") {
    return (
      <img
        src={asset.value}
        alt=""
        className="block size-5 shrink-0 rounded-md object-cover ring-1 ring-black/30"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="block size-5 shrink-0 rounded-md ring-1 ring-black/30"
      style={{ backgroundColor: asset.value }}
    />
  );
}

export function ColorAssetPicker({
  assets,
  isLoading,
  isError,
  selectedAssetId,
  onSelect,
  onRetry,
  emptyMessage = EMPTY_MESSAGE.colorAsset,
  projectAssetIds,
  onPreview,
}: ColorAssetPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = assets.find((a) => a.id === selectedAssetId) ?? null;

  const results = useMemo(
    () => searchAssets(assets, query).slice(0, RESULT_CAP),
    [assets, query],
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    // Focus the search box when the dropdown opens (avoids the autoFocus a11y rule).
    inputRef.current?.focus();
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setIsOpen(false);
      }
    };
    window.addEventListener("mousedown", handleClick);
    window.addEventListener("keydown", handleKey, true);
    return () => {
      window.removeEventListener("mousedown", handleClick);
      window.removeEventListener("keydown", handleKey, true);
    };
  }, [isOpen]);

  return (
    <div className="mt-5" ref={containerRef}>
      <h3 className="text-sm font-bold text-white">Saved Assets</h3>
      <div className="relative mt-3">
        <button
          type="button"
          aria-expanded={isOpen}
          onClick={() => setIsOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-3 rounded-lg border border-black/25 bg-[var(--bg-field)] px-3 py-2.5 text-left text-sm text-[var(--text-primary)] outline-none focus:ring-2 focus:ring-white/25"
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected ? (
              <>
                <AssetThumb asset={selected} />
                <span className="truncate">{selected.name}</span>
              </>
            ) : (
              <span className="text-[var(--text-secondary)]">
                Select an asset
              </span>
            )}
          </span>
          <CaretDownIcon size={16} weight="bold" className="shrink-0" />
        </button>

        {isOpen && (
          <div className="mt-2 rounded-lg border border-black/25 bg-[var(--bg-field)] py-2 shadow-xl">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search assets"
              placeholder="Search by name…"
              className="mx-2 mb-1 w-[calc(100%-1rem)] rounded-md border border-black/25 bg-[var(--bg-field)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] outline-none focus:ring-2 focus:ring-white/25"
            />
            <div className="max-h-64 overflow-auto">
              {!(isLoading || isError) &&
              query.trim() &&
              results.length === 0 ? (
                <p className="px-3 py-2 text-sm text-[var(--text-secondary)]">
                  No matches.
                </p>
              ) : (
                <ColorAssetPickerPanel
                  assets={results}
                  isLoading={isLoading}
                  isError={isError}
                  selectedAssetId={selectedAssetId}
                  onSelect={(asset) => {
                    onSelect(asset);
                    setIsOpen(false);
                  }}
                  onRetry={onRetry}
                  emptyMessage={emptyMessage}
                  projectAssetIds={projectAssetIds}
                  onPreview={onPreview}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ColorAssetPickerPanel({
  assets,
  isLoading,
  isError,
  selectedAssetId,
  onSelect,
  onRetry,
  emptyMessage,
  projectAssetIds,
  onPreview,
}: ColorAssetPickerProps) {
  if (isLoading) {
    return (
      <p className="px-3 py-2 text-sm text-[var(--text-secondary)]">
        Loading your assets…
      </p>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <span className="text-sm text-red-400">Couldn't load assets.</span>
        <button
          type="button"
          onClick={onRetry}
          className="text-sm font-semibold text-[var(--accent-primary)] hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (assets.length === 0) {
    return (
      <p className="px-3 py-2 text-sm text-[var(--text-secondary)]">
        {emptyMessage}
      </p>
    );
  }

  if (!projectAssetIds) {
    return (
      <AssetRows
        assets={assets}
        selectedAssetId={selectedAssetId}
        onSelect={onSelect}
        onPreview={onPreview}
      />
    );
  }

  // Assets already on this project come first; the full library stays below.
  const inFolder = assets.filter((a) => projectAssetIds.has(a.id));
  return (
    <>
      {inFolder.length > 0 && (
        <>
          <AssetSectionHeading>Assets in this folder</AssetSectionHeading>
          <AssetRows
            assets={inFolder}
            selectedAssetId={selectedAssetId}
            onSelect={onSelect}
            onPreview={onPreview}
          />
        </>
      )}
      <AssetSectionHeading>All Assets</AssetSectionHeading>
      <AssetRows
        assets={assets}
        selectedAssetId={selectedAssetId}
        onSelect={onSelect}
        onPreview={onPreview}
      />
    </>
  );
}

function AssetSectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h4 className="px-3 pt-2 pb-1 text-sm font-bold text-white">{children}</h4>
  );
}

function AssetRows({
  assets,
  selectedAssetId,
  onSelect,
  onPreview,
}: Pick<
  ColorAssetPickerProps,
  "assets" | "selectedAssetId" | "onSelect" | "onPreview"
>) {
  return (
    <ul className="flex flex-col">
      {assets.map((asset) => {
        const isSelected = asset.id === selectedAssetId;
        return (
          <li key={asset.id} className="flex items-center">
            <button
              type="button"
              aria-pressed={isSelected}
              onClick={() => onSelect(asset)}
              className={`flex min-w-0 flex-1 items-center gap-3 px-3 py-2 text-left text-sm text-[var(--text-primary)] hover:bg-white/5 ${
                isSelected ? "bg-white/10" : ""
              }`}
            >
              <AssetThumb asset={asset} />
              <span className="truncate">
                {asset.name}
                {asset.manufacturer && (
                  <span className="text-[var(--text-secondary)]">
                    {" "}
                    ({asset.manufacturer})
                  </span>
                )}
              </span>
            </button>
            {onPreview && asset.type === "image" && (
              <button
                type="button"
                onClick={() => onPreview(asset)}
                aria-label={`Preview ${asset.name}`}
                className="shrink-0 px-3 py-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              >
                <EyeIcon size={16} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
